import { useState } from 'react'
import { useDispatch } from 'react-redux'
import { Select, Modal, message, Alert, Button } from 'antd'
import { ApolloError } from '@apollo/client'

import Box from '~/core/Box'
import Text from '~/core/Text'
import logger from '~/lib/logger'
import getColor from '~/lib/getColor'
import { openModal } from '~/redux-modules/modals'
import { UserTeamFragment } from '~/gql/graphql'

import { useTeamsMoveMutation } from './logic'

interface MoveQuoteOrderModalPropsType {
    onCancel: (teamData?: { id: string; name?: string }) => void
    id: string
    name: string
    typeName: 'Quote' | 'SalesOrder'
    visible: boolean
    userId: number
    teams: UserTeamFragment[]
    userTeamId?: number
    isTeamsDisabled: boolean
}

export const MoveQuoteOrderModal = (props: MoveQuoteOrderModalPropsType) => {
    const {
        onCancel,
        id,
        name,
        visible,
        teams,
        typeName,
        userId,
        userTeamId,
        isTeamsDisabled,
    } = props

    const dispatch = useDispatch()

    const [teamId, setTeamId] = useState<string>('')
    const [teamName, setTeamName] = useState<string | undefined>('')
    const [movingQuote, setMovingQuote] = useState<boolean>(false)

    const isQuote = typeName === 'Quote'
    const quoteOrOrder = isQuote ? 'quote' : 'order'

    const onCompleted = () => {
        onCancel({ id: teamId, name: teamName })
    }

    const onError = (err: ApolloError) => {
        const errorMessage = `Failed to add ${quoteOrOrder} to ${teamName} team`

        message.error(errorMessage)
        logger.exception(err, {
            extra: {
                info: errorMessage,
                teamId,
                userId,
                id,
            },
        })
        setMovingQuote(false)
    }

    const [moveToTeam] = useTeamsMoveMutation(id, userId, onCompleted, onError)
    const onChange = (value: string) => {
        const selectedTeam = teams.find(team => team.id === +value)

        setTeamId(value)
        setTeamName(selectedTeam?.name)
    }

    const dropdownPlaceholder =
        teams.length === 0
            ? 'No teams available'
            : `Select ${quoteOrOrder} destination`

    return (
        <Modal
            title={`Add ${name} to a workspace`}
            maskClosable
            onCancel={() => onCancel()}
            onOk={() => {
                setMovingQuote(true)
                moveToTeam({
                    variables: {
                        teamId,
                    },
                })
            }}
            confirmLoading={movingQuote}
            okText="Add"
            visible={visible}
            okButtonProps={{
                disabled: !teamId,
            }}
            width="630px"
            bodyStyle={{ maxHeight: '676px', overflow: 'auto' }}
        >
            <Box display="flex" direction="col">
                <Text
                    color="blackAlpha65"
                    fontSize="16px"
                    fontWeight={400}
                    style={{ marginBottom: '19px' }}
                >
                    Add this {quoteOrOrder} to a workspace to give its members
                    view and edit access.
                </Text>
            </Box>
            <Box display="block">
                {isTeamsDisabled ? (
                    <Alert
                        description={
                            <Box display="flex" direction="col">
                                <Text
                                    fontWeight={300}
                                    fontSize="14px"
                                    color={getColor('blackAlpha65')}
                                    style={{
                                        marginBottom: '10px',
                                        lineHeight: '22px',
                                    }}
                                >
                                    To share orders with team members you must
                                    be subscribed to Fictiv Premium. Fictiv
                                    Premium unlocks visibility across teams with
                                    simplified sharing, advanced lead time
                                    optimization, and preferred pricing.
                                </Text>
                                <Button
                                    type="primary"
                                    onClick={() => {
                                        onCancel()
                                        dispatch(openModal('UPSELL'))
                                    }}
                                    data-test-target="add-user-to-team-upgrade-to-fictiv-premium-button"
                                >
                                    Upgrade to Fictiv Premium
                                </Button>
                            </Box>
                        }
                        type="info"
                    />
                ) : (
                    <Select
                        style={{ width: 300, marginTop: '20px' }}
                        placeholder={dropdownPlaceholder}
                        onChange={onChange}
                        notFoundContent="Your teams will show up here"
                    >
                        {teams.map(team => (
                            <Select.Option
                                key={team.id}
                                value={team.id}
                                disabled={team.id === userTeamId}
                            >
                                {team.name}
                                {team.id === userTeamId && ' (already added)'}
                            </Select.Option>
                        ))}
                    </Select>
                )}
            </Box>
            <Box display="block" style={{ marginTop: '20px' }}>
                <Text color="blackAlpha45">
                    Note: Collaborators will still have access once the{' '}
                    {quoteOrOrder} is added.
                </Text>
            </Box>
        </Modal>
    )
}
